/** @jsxImportSource @emotion/react */

import {
  EuiButton,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingChart,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { CallOutError } from "components/CallOutError/CallOutError";
import {
  ToolEditorBundleFileState,
  toolEditorDropZoneHeight,
} from "./ToolEditorFormCodeUploadBody";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { css } from "@emotion/react";

export interface ToolEditorFormCodeFileStateProps {
  fileState: ToolEditorBundleFileState;
}
export const ToolEditorFormCodeFileState = ({
  fileState,
}: ToolEditorFormCodeFileStateProps) => {
  const hasTestTable = useToolEditorContext((s) => s.testTable !== null);

  switch (fileState.state) {
    case "ERROR": {
      return (
        <CallOutError
          suppressContactSupport
          message={
            fileState.errorType === "INVALID_BUNDLE"
              ? (fileState.errorMessage ??
                "This file is not a valid IDEAS Code Bundle.")
              : (fileState.errorMessage ??
                "Unknown Error Reading Bundle. If this issue persists please contact support.")
          }
        />
      );
    }
    case "LOADED_COMPATIBLE": {
      return (
        <EuiFlexGroup
          direction="column"
          alignItems="center"
          gutterSize="s"
          justifyContent="center"
        >
          <EuiCallOut
            color="warning"
            title="Do you wish to proceed with upload?"
            style={{ width: "100%" }}
          >
            <EuiText>
              Your function signature is identical and your parameter
              configuration will be applied to the new bundle, but your test
              table will be reset when uploading a new bundle.
            </EuiText>
            <EuiSpacer />
            <EuiFlexGroup>
              <EuiButton onClick={fileState.onConfirmUpload}>Accept</EuiButton>
              <EuiButton onClick={fileState.onCancelUpload}>Cancel</EuiButton>
            </EuiFlexGroup>
          </EuiCallOut>
        </EuiFlexGroup>
      );
    }
    case "LOADING": {
      return (
        <EuiFlexGroup
          direction="column"
          alignItems="center"
          justifyContent="center"
          css={css`
            height: ${toolEditorDropZoneHeight}px;
          `}
        >
          <EuiFlexItem grow={false}>
            <EuiLoadingChart aria-label="Loading..." size="xl" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText>Reading Bundle Metadata</EuiText>{" "}
          </EuiFlexItem>
        </EuiFlexGroup>
      );
    }
    case "LOADED_INCOMPATIBLE": {
      return (
        <EuiFlexGroup
          direction="column"
          alignItems="center"
          gutterSize="s"
          justifyContent="center"
        >
          <EuiCallOut
            color="warning"
            title="Do you wish to proceed with upload?"
            style={{ width: "100%" }}
          >
            <EuiText>
              The code bundle you are attempting to upload has a different code
              signature than the current code bundle on this tool version. If
              you proceed with upload, the following migrations will be applied
              to your spec:
              {hasTestTable &&
                "Explain that test table will be deleted as well."}
            </EuiText>
            <EuiSpacer />

            <table style={{ border: "1px solid lightgrey", padding: 5 }}>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    border: "1px solid lightgrey",
                    padding: 5,
                  }}
                >
                  Parameter Key
                </th>
                <th
                  style={{
                    textAlign: "left",
                    border: "1px solid lightgrey",
                    padding: 5,
                  }}
                >
                  Resolution
                </th>
              </tr>
              {fileState.migrationDetails.map((detail) => {
                return (
                  <tr key={detail.key + detail.detail}>
                    <td
                      style={{
                        paddingRight: 20,
                        border: "1px solid lightgrey",
                        padding: 5,
                      }}
                    >
                      {detail.key}
                    </td>
                    <td
                      style={{
                        paddingRight: 20,
                        border: "1px solid lightgrey",
                        padding: 5,
                      }}
                    >
                      {detail.detail}
                    </td>
                  </tr>
                );
              })}
            </table>
            <EuiSpacer />
            <EuiFlexGroup>
              <EuiButton onClick={fileState.onConfirmUpload}>Accept</EuiButton>
              <EuiButton onClick={fileState.onCancelUpload}>Cancel</EuiButton>
            </EuiFlexGroup>
          </EuiCallOut>
        </EuiFlexGroup>
      );
    }
  }
};
